import React from "react";
import { Helmet } from "react-helmet";
import { colors, serif } from "../../theme/brand";
import useMediaQuery from "../../hooks/useMediaQuery";
import photo from "../../assets/robert-jan.png";

const tags = [
  "AI- & LLM-engineering",
  "Solution architecture",
  "Platform engineering",
  "Backend & API's",
  "Data & analytics",
  "GCP · Azure · AWS",
];

const experience = [
  {
    period: "2025 – nu",
    client: "Change CX",
    role: "Principal AI & Platform Engineer / Solution Architect",
    body: "Technisch eigenaar van een AI-platform dat dagelijks meer dan 35.000 klantgesprekken analyseert. Denkt mee over AI-, data- en platformstrategie in een organisatie van zo'n 700 medewerkers en is technisch gesprekspartner in sales- en klanttrajecten.",
  },
  {
    period: "2023 – nu",
    client: "GrandVision",
    role: "Senior Software Engineer · Cart, Payments & Orders",
    body: "Eindverantwoordelijk voor de domeinen winkelwagen, betalingen en orders op een wereldwijd e-commerce SaaS-platform voor meer dan 25 operating companies.",
  },
  {
    period: "2024 – 2026",
    client: "Booming Jobs",
    role: "Technical Lead",
    body: "Backend en GCP-platform vanaf nul, zoeken en matchen met BERT-embeddings en Typesense, en het onboarden en begeleiden van nieuwe engineers.",
  },
  {
    period: "2022 – 2024",
    client: "Beecot",
    role: "Technical Lead",
    body: "Architectuur, backend en AWS-infrastructuur vanaf nul opgezet, en het platform daarna overgedragen aan een zelf opgeleid intern team.",
  },
  {
    period: "2022 – 2023",
    client: "PIDZ",
    role: "Technical Lead",
    body: "Leiding over de migratie van twee monolieten naar een cloud-native NestJS-platform op Kubernetes.",
  },
  {
    period: "2019 – 2022",
    client: "VodafoneZiggo",
    role: "Senior Software Engineer · Full-Stack Engineer",
    body: "Legde de Backend-for-Frontend-architectuur en de API-standaarden van het development chapter vast, bouwde de integratielaag voor MyZiggo en ontwikkelde een eigen tag-managementplatform ter vervanging van Relay42.",
  },
  {
    period: "2016 – 2019",
    client: "Entelect · IDAS · Fixx iT",
    role: "Front-end Web Developer",
    body: "Complete webapplicaties en SDK's voor externe partners, onder meer voor het BerekenAutokosten-platform.",
  },
];

const values = [
  { title: "Eigenaarschap", body: "We nemen verantwoordelijkheid voor het hele traject, niet alleen ons stukje ervan." },
  { title: "Het juiste team", body: "Per project stellen we samen wie er nodig is — geen vaste overhead." },
  { title: "Bouwen én beheren", body: "We leveren niet alleen op, we houden het draaiend en betrouwbaar." },
  { title: "Helder & warm", body: "Directe communicatie zonder jargon. Je weet altijd waar je staat." },
];

export default function AppOverOns() {
  const isMobile = useMediaQuery("(max-width: 860px)");

  return (
    <div style={{ animation: "fadeUp .5s ease both" }}>
      <Helmet>
        <title>Over ons — Datails</title>
        <meta
          name="description"
          content="Achter Datails staat Robert-Jan Kuyper: principal engineer en solution architect met ruim 11 jaar ervaring in AI, cloud, platform engineering en bedrijfskritische backends."
        />
      </Helmet>

      <section style={{ maxWidth: 1120, margin: "0 auto", padding: isMobile ? "56px 24px" : "88px 32px" }}>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.3fr 1fr", gap: isMobile ? 40 : 64, alignItems: "start" }}>
          <div>
            <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.teal, marginBottom: 16 }}>
              Over ons
            </p>
            <h1 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(38px,5vw,62px)", lineHeight: 1.04, letterSpacing: "-0.015em", textWrap: "balance" }}>
              Geen klassiek bureau. Een partner die eigenaarschap neemt.
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.65, color: colors.text, marginTop: 28 }}>
              Datails is opgezet vanuit een simpel idee: organisaties verdienen een technische partner die niet alleen
              adviseert, maar ook levert — en verantwoordelijk blijft voor het resultaat.
            </p>
            <p style={{ fontSize: 18, lineHeight: 1.65, color: colors.text, marginTop: 18 }}>
              Achter Datails staat <strong style={{ fontWeight: 600, color: colors.ink }}>Robert-Jan Kuyper</strong>:
              principal engineer en solution architect met ruim 11 jaar ervaring in bedrijfskritische softwareplatformen.
              Hij begon als front-end developer en werkt nu aan backend, cloudarchitectuur, platform engineering, data en
              AI. Juist die breedte maakt dat we een project van begin tot eind kunnen overzien, van de infrastructuur
              tot de AI-laag erbovenop.
            </p>
            <p style={{ fontSize: 18, lineHeight: 1.65, color: colors.text, marginTop: 18 }}>
              Met een bachelor Bedrijfskunde op zak voelt hij zich net zo thuis aan tafel met directie, product en sales
              als in de code. Hij vertaalt businessvragen naar schaalbare oplossingen en blijft zelf meebouwen. Waar meer
              handen of specialismen nodig zijn, stellen we per opdracht een team samen uit een vertrouwd netwerk: de
              juiste mensen, precies wanneer nodig.
            </p>
            <p style={{ fontSize: 18, lineHeight: 1.65, color: colors.text, marginTop: 18 }}>
              Zo krijg je de betrokkenheid van een vast aanspreekpunt met de slagkracht van een team dat met je project
              meeschaalt.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 26 }}>
              {tags.map((t) => (
                <span
                  key={t}
                  style={{ padding: "9px 15px", border: `1px solid ${colors.border}`, background: colors.panel, borderRadius: 999, fontSize: 14.5, fontWeight: 600, color: colors.ink }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
          <div style={{ maxWidth: isMobile ? 280 : "none" }}>
            <div style={{ borderRadius: 18, overflow: "hidden", background: "#E9E4D6", border: `1px solid ${colors.border}` }}>
              <img src={photo} alt="Robert-Jan Kuyper" style={{ width: "100%", display: "block", background: "#E9E4D6" }} />
            </div>
            <p style={{ fontSize: 15, color: colors.textMuted, marginTop: 14, lineHeight: 1.5 }}>
              <strong style={{ color: colors.ink, fontWeight: 600 }}>Robert-Jan Kuyper</strong>
              <br />
              Oprichter · Arnhem, Nederland
            </p>
          </div>
        </div>
      </section>

      <section style={{ background: colors.panel, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "76px 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 24 }}>
            {values.map((v) => (
              <div key={v.title}>
                <h3 style={{ fontSize: 19, fontWeight: 600, marginBottom: 10 }}>{v.title}</h3>
                <p style={{ fontSize: 16, lineHeight: 1.55, color: colors.textMuted }}>{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ maxWidth: 1120, margin: "0 auto", padding: isMobile ? "64px 24px" : "88px 32px" }}>
        <p style={{ fontSize: 14, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.teal, marginBottom: 14 }}>
          Ervaring
        </p>
        <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(32px,4vw,48px)", lineHeight: 1.08, letterSpacing: "-0.01em", maxWidth: 720, marginBottom: 44 }}>
          Ruim 11 jaar bouwen aan platformen die ertoe doen.
        </h2>
        <div style={{ borderTop: `1px solid ${colors.border}` }}>
          {experience.map((e) => (
            <div
              key={e.client}
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "160px 1fr",
                gap: isMobile ? 6 : 32,
                padding: "28px 0",
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, color: colors.textFaint, paddingTop: 4 }}>{e.period}</div>
              <div style={{ maxWidth: 720 }}>
                <h3 style={{ fontSize: 20, fontWeight: 600 }}>
                  {e.client}
                  <span style={{ fontWeight: 400, color: colors.textMuted }}> · {e.role}</span>
                </h3>
                <p style={{ fontSize: 16, lineHeight: 1.6, color: colors.textMuted, marginTop: 8 }}>{e.body}</p>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 15, lineHeight: 1.6, color: colors.textMuted, marginTop: 28 }}>
          <strong style={{ color: colors.ink, fontWeight: 600 }}>Opleiding & certificering</strong> · Bachelor of Business
          Administration (Saxion) · Microsoft 70-480 · Docker Fundamentals · Kubernetes Fundamentals
        </p>
      </section>

      <section style={{ background: colors.panel, borderTop: `1px solid ${colors.border}` }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", padding: "88px 32px", textAlign: "center" }}>
        <h2 style={{ fontFamily: serif, fontWeight: 400, fontSize: "clamp(32px,4.5vw,52px)", lineHeight: 1.06, maxWidth: 680, margin: "0 auto" }}>
          Laten we kennismaken.
        </h2>
        <a
          href="mailto:info@datails.nl"
          style={{ display: "inline-flex", alignItems: "center", gap: 9, background: colors.teal, color: "#FBF7EF", padding: "16px 30px", borderRadius: 999, fontSize: 17, fontWeight: 600, marginTop: 28 }}
        >
          info@datails.nl
        </a>
        </div>
      </section>
    </div>
  );
}
